import { ExpandMore } from '@mui/icons-material';
import { ButtonBase, ListSubheader, MenuItem, MenuList, Popover, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef, useState } from 'react';

import { StatusBadge } from '@/tracker/entities/status';
import { TaskStatusRef, TaskStatusType } from '@/tracker/types';

import s from './StatusSelect.module.scss';

const STATUS_TYPE_GROUPS: { type: TaskStatusType; label: string }[] = [
  { type: 'new', label: 'Новые' },
  { type: 'in_progress', label: 'В работе' },
  { type: 'paused', label: 'Приостановлены' },
  { type: 'done', label: 'Завершены' },
  { type: 'cancelled', label: 'Отменены' },
];

type Props = {
  status: TaskStatusRef;
  /** Статусы очереди, переходы между ними свободные. null — ещё не загружены, выбор недоступен */
  statuses: TaskStatusRef[] | null;
  disabled: boolean;
  onChange: (statusKey: string) => void;
};

const StatusSelect: React.FC<Props> = observer(({ status, statuses, disabled, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLUListElement>(null);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    // Порядок внутри группы — как в очереди
    const matching = (statuses ?? []).filter(
      (option) => option.key !== status.key && option.name.toLowerCase().includes(needle),
    );

    return STATUS_TYPE_GROUPS.map((group) => ({
      ...group,
      statuses: matching.filter((option) => option.type === group.type),
    })).filter((group) => group.statuses.length > 0);
  }, [statuses, status.key, query]);

  const close = () => {
    setAnchor(null);
    setQuery('');
  };

  const pick = (statusKey: string) => {
    close();
    onChange(statusKey);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    const first = groups[0]?.statuses[0];

    if (event.key === 'Enter' && first) pick(first.key);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      listRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
  };

  return (
    <>
      <ButtonBase
        disabled={disabled || !statuses?.length}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-label={`Статус: ${status.name}. Сменить статус`}
        sx={{ gap: 0.5, borderRadius: 1 }}
      >
        <StatusBadge status={status} />
        <ExpandMore fontSize="small" sx={{ color: 'var(--dark-secondary-color)' }} />
      </ButtonBase>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div className={s.picker}>
          <TextField
            autoFocus
            size="small"
            placeholder="Перейти в статус"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            slotProps={{ htmlInput: { 'aria-label': 'Найти статус' } }}
          />
          <MenuList ref={listRef} dense className={s.picker__list} aria-label="Статусы">
            {groups.flatMap((group) => [
              <ListSubheader
                // Префиксы: ключи статусов (new, cancelled) могут совпасть с типами групп
                key={`group-${group.type}`}
                sx={{ lineHeight: '28px', fontSize: 12, color: 'var(--secondary-color)' }}
              >
                {group.label}
              </ListSubheader>,
              ...group.statuses.map((option) => (
                <MenuItem key={`status-${option.key}`} onClick={() => pick(option.key)}>
                  {option.name}
                </MenuItem>
              )),
            ])}
            {groups.length === 0 && <MenuItem disabled>Нет такого статуса</MenuItem>}
          </MenuList>
        </div>
      </Popover>
    </>
  );
});

export { StatusSelect };
